import { FC } from "react";

import { Button } from "capitalrx-components-2.0/lib/general";

import { Finance } from "@/api/interfaces";
import { currentEnvironment } from "@/utils";
import type { SortField, UiSortDirection } from "./PaymentDropdown";

interface ProviderPaymentsDemoButtonProps {
  disabled: boolean;
  isActive: boolean;
  onClick: () => void;
}

export const isProviderPaymentsDemoEnabled = ["development", "staging"].includes(currentEnvironment);

const makeDemoClaim = (
  claim: Finance.MedicalClaimProviderSchema,
): Finance.MedicalClaimProviderReportList => ({
  provider_medical_claim_payment_report: claim,
});

export const DEMO_UPCOMING_CLAIMS: Finance.MedicalClaimProviderReportList[] = [
  makeDemoClaim({
    medical_claim_id: "MC-DEMO-1001",
    medical_claim_payment_status_id: "pending",
    billing_provider_npi: "1234567890",
    external_member_id: "JH10004567",
    start_date: "2026-10-03",
    submission_date: "2026-10-08",
    claim_status: "in_progress",
    payment_amount: 0,
    issue_date: "",
    payment_is_void: false,
    payment_is_cleared: false,
    payment_status: "upcoming",
    patient_name: "Jordan Avery",
    patient_account: "PA-44901",
    patient_pay_amount: 42.5,
  }),
  makeDemoClaim({
    medical_claim_id: "MC-DEMO-1002",
    medical_claim_payment_status_id: "scheduled",
    billing_provider_npi: "1234567890",
    external_member_id: "JH10007891",
    start_date: "2026-10-11",
    submission_date: "2026-10-13",
    claim_status: "approved",
    payment_amount: 315.9,
    issue_date: "2026-10-24",
    payment_is_void: false,
    payment_is_cleared: false,
    payment_status: "scheduled",
    patient_name: "Casey Morgan",
    patient_account: "PA-44902",
    patient_pay_amount: 18.25,
  }),
];

export const DEMO_ISSUED_TRANSACTIONS: Finance.MedicalPaymentTransactionSchema[] = [
  {
    transaction_id: "835000421",
    medical_835_record_id: "EOP-DEMO-421",
    funding_ledger_amount: 1442.73,
    funding_ledger_type: "ACH",
    settlement_date: "2026-09-11 00:00:00",
    medical_claim_payment_results: [
      makeDemoClaim({
        medical_claim_id: "MC-DEMO-2001",
        medical_claim_payment_status_id: "paid",
        billing_provider_npi: "1234567890",
        external_member_id: "JH10001111",
        start_date: "2026-08-26",
        submission_date: "2026-08-29",
        claim_status: "approved",
        payment_amount: 780.45,
        issue_date: "2026-09-11",
        payment_is_void: false,
        payment_is_cleared: true,
        payment_status: "cleared",
        patient_name: "Taylor Blake",
        patient_account: "PA-54001",
        patient_pay_amount: 22,
      }),
      makeDemoClaim({
        medical_claim_id: "MC-DEMO-2002",
        medical_claim_payment_status_id: "paid",
        billing_provider_npi: "1234567890",
        external_member_id: "JH10002222",
        start_date: "2026-08-31",
        submission_date: "2026-09-02",
        claim_status: "partially_approved",
        payment_amount: 662.28,
        issue_date: "2026-09-11",
        payment_is_void: false,
        payment_is_cleared: true,
        payment_status: "cleared",
        patient_name: "Morgan Ellis",
        patient_account: "PA-54002",
        patient_pay_amount: 97.5,
      }),
    ],
  },
  {
    transaction_id: "835000399",
    medical_835_record_id: "EOP-DEMO-399",
    funding_ledger_amount: 534.12,
    funding_ledger_type: "Check",
    settlement_date: "2026-08-21 00:00:00",
    medical_claim_payment_results: [
      makeDemoClaim({
        medical_claim_id: "MC-DEMO-3001",
        medical_claim_payment_status_id: "voided",
        billing_provider_npi: "1234567890",
        external_member_id: "JH10003333",
        start_date: "2026-07-18",
        submission_date: "2026-07-25",
        claim_status: "denied",
        payment_amount: 534.12,
        issue_date: "2026-08-21",
        payment_is_void: true,
        payment_is_cleared: false,
        payment_status: "issued",
        patient_name: "Riley Chen",
        patient_account: "PA-54003",
        patient_pay_amount: 0,
      }),
    ],
  },
];

const getDemoSortValue = (
  claim: Finance.MedicalClaimProviderReportList,
  field: SortField,
): number | string => {
  const report = claim.provider_medical_claim_payment_report;

  switch (field) {
    case "start_date":
      return Date.parse(report.start_date);
    case "last_name":
      return report.patient_name.split(" ").at(-1)?.toLowerCase() ?? "";
    case "submission_date":
      return Date.parse(report.submission_date);
    case "status":
      return report.claim_status.toLowerCase();
    default:
      return "";
  }
};

export const sortProviderPaymentsDemoClaims = (
  claims: Finance.MedicalClaimProviderReportList[],
  field: SortField | null,
  direction: UiSortDirection,
): Finance.MedicalClaimProviderReportList[] => {
  if (!field || !direction) return claims;

  const directionMultiplier = direction === "ascending" ? 1 : -1;

  return [...claims].sort((left, right) => {
    const leftValue = getDemoSortValue(left, field);
    const rightValue = getDemoSortValue(right, field);

    if (leftValue < rightValue) return -1 * directionMultiplier;
    if (leftValue > rightValue) return directionMultiplier;
    return 0;
  });
};

export const ProviderPaymentsDemoButton: FC<ProviderPaymentsDemoButtonProps> = ({
  disabled,
  isActive,
  onClick,
}) => {
  if (!isProviderPaymentsDemoEnabled) return null;

  return (
    <Button appearance="secondary" icon="bolt" onClick={onClick} disabled={disabled}>
      {isActive ? "Exit Demo" : "Demo"}
    </Button>
  );
};
